"use client";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useField, useFormikContext } from "formik";
import { ReactNode, useRef, useEffect, useState } from "react";

interface CheckboxProps {
  label: string;
  name: string;
  children?: ReactNode;
  disabled?: boolean;
  className?: string;
  onChange?: (val: boolean) => void;
}

export function CheckboxInput({
  label,
  name,
  children,
  className,
  onChange,
  disabled = false,
}: CheckboxProps) {
  const [field, meta, helpers] = useField(name);
  const { setFieldValue } = useFormikContext();
  const inputRef = useRef<HTMLDivElement>(null);

  const [shouldShowError, setShouldShowError] = useState(false);
  const hasError = shouldShowError && meta.error;

  useEffect(() => {
    if (hasError && inputRef.current) {
      inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [hasError]);

  const { submitCount } = useFormikContext();
  useEffect(() => {
    if (submitCount > 0 && meta.error) setShouldShowError(true);
  }, [submitCount, meta.error]);

  return (
    <div className={cn("flex flex-col w-full", className)} ref={inputRef}>
      <div className="flex items-start space-x-2">
        <Checkbox
          id={name}
          checked={field.value}
          disabled={disabled}
          onBlur={() => helpers.setTouched(true)}
          onCheckedChange={(checked: boolean) => {
            setFieldValue(name, checked);
            setShouldShowError(!checked);
            onChange?.(checked);
          }}
          className="h-4 w-4 flex-shrink-0"
        />

        <label
          htmlFor={name}
          className="flex flex-col text-xs lg:text-sm cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {label && <span>{label}</span>}
          {children && <span className="text-xs">{children}</span>}
        </label>
      </div>

      {hasError && (
        <p className="text-red-500 text-xs mt-1">{meta.error}</p>
      )}
    </div>
  );
}
